import { useCallback, useEffect, useState } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { PanelProduct, PanelType } from '../types';

import { getDefaultPanelWidth } from './utils';

export const usePanelWidth = () => {
	const [defaultWidth, setDefaultWidth] = useState(getDefaultPanelWidth);
	const [panelWidth, setPanelWidth] = useState(defaultWidth);
	const [manuallyResized, setManuallyResized] = useState(false);
	const [panelOpen, setPanelOpen] = useState(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onPanelWidthChanged = useCallback(
		(
			newPanelWidth: number,
			panelProduct: PanelProduct | undefined,
			panelType: PanelType | undefined,
			parentProduct: string,
		) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					product: panelProduct,
					source: 'preview-panel',
					action: 'resize',
					actionSubject: 'resizer',
					actionSubjectId: 'previewPanelResizer',
					attributes: {
						initialPanelWidth: panelWidth,
						newPanelWidth,
						newWidthSmaller: newPanelWidth < panelWidth,
						parentProduct,
						panelType: panelType,
						windowWidth: window.innerWidth,
						windowHeight: window.innerHeight,
					},
				},
			}).fire();

			setPanelWidth(newPanelWidth);
			setManuallyResized(true);
		},
		[createAnalyticsEvent, panelWidth],
	);

	const onPanelOpened = useCallback(
		(openWidth?: number) => {
			const width = openWidth ?? (manuallyResized ? panelWidth : defaultWidth);
			setPanelWidth(width);
			setPanelOpen(true);
		},
		[defaultWidth, manuallyResized, panelWidth],
	);

	const onPanelClosed = useCallback(() => {
		setPanelOpen(false);
	}, []);

	// getDefaultPanelWidth does not dependencies related to this hook
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const windowResizeListener = useCallback(
		debounce(() => {
			if (!panelOpen) {
				setDefaultWidth(getDefaultPanelWidth());
				setManuallyResized(false);
			}
		}, 500),
		[panelOpen],
	);

	useEffect(() => {
		const unbind: UnbindFn = bind(window, {
			type: 'resize',
			listener: windowResizeListener,
		});

		return () => {
			unbind();
		};
	}, [windowResizeListener]);

	return {
		panelWidth,
		onPanelWidthChanged,
		onPanelOpened,
		onPanelClosed,
	};
};
